import React from 'react';
import s from './InvoicePreviewInfo.scss';
import { Layout, Text, Heading, Divider, Cell } from '@wix/design-system';
import LineItems from '../../DocParts/LineItems';
import Totals from '../Totals';
import Customer from '../Customer/Customer';
import Comments from '../../DocParts/Comments';
import { InvoicesBusinessInfo } from '../../DocParts/InvoicesBusinessInfo/InvoicesBusinessInfo';
import InvoiceInfo from '../InvoiceInfo';
import Status from '../Status';
import { isStatusDisplayed } from '../Status/status-utils';
import AscendLogo from '../../AscendLogo';
import { withTranslation } from '@wix/fe-essentials/i18n';
import RecurringInvoiceView from '../../RecurringInvoicesView/RecurringInvoicesView';
import { InvoiceStatus, } from '@wix/ambassador-wix-quotes-web/types';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import { WithTitleLoader } from '../../loaders/loaders-builder';
import { isMobileView } from '../../../core/isMobileView';
import { Logo } from '../../DocParts/Logo/Logo';
import { PaymentsReceived } from '../../DocParts/PaymentsReceived';
import { DEFAULT_CURRENCY_CODE, DEFAULT_LOCALE } from '../../../core';
import { parseFromHtmlString } from '../../../utils/parsing';
const Title = WithTitleLoader(({ invoice }) => (React.createElement("div", { className: s.title, style: { marginBottom: '8px' } },
    React.createElement("div", { className: s.mobileHide },
        React.createElement(Heading, { className: s.title, size: "large", dataHook: "invoice-title" }, invoice.title)),
    React.createElement("div", { className: s.mobileShow },
        React.createElement(Text, { dataHook: "invoice-title", weight: "bold", size: "tiny" }, invoice.title)))));
const PreviewInfo = ({ invoice, businessInfo, status = InvoiceStatus.Draft, recurringInvoiceViewEnabled, recurringStandaloneViewEnabled, isUoU, ascend, darkView, t, onInvoiceReady, businessCustomFields, documentCustomFields, footer, customInvoiceNumberLabel, customFields, primarySiteLocale, showTaxPerLineItem, showTaxBreakdown, showItemsSubtotalPerTax, showTransactionDetails, taxAdjustmentsEnabled, taxSettingsEnabled, paymentsSectionEnabled, harmonyThemeEnabled, }) => {
    const [businessInfoReady, setBusinessInfoReady] = React.useState(false);
    const [invoiceReady, setInvoiceReady] = React.useState(false);
    const lineItems = invoice.lineItems?.lineItems ?? [];
    React.useEffect(() => {
        !!businessInfo && !isEmpty(businessInfo) && setBusinessInfoReady(true);
        !!invoice && !isEmpty(invoice) && setInvoiceReady(true);
    }, [businessInfo, invoice]);
    const lineItemList = lineItems.map(({ name = '', description = '', price, quantity, taxes, taxedTotal, }) => {
        const lineItemQuantity = quantity?.serializedValue || 0;
        const lineItemPrice = price?.serializedValue || 0;
        const lineItemTotal = lineItemQuantity * lineItemPrice;
        return {
            name,
            description: parseFromHtmlString(description, ''),
            price: lineItemPrice,
            quantity: lineItemQuantity,
            total: showTaxPerLineItem
                ? taxedTotal?.serializedValue ?? 0
                : lineItemTotal,
            taxes: taxes ?? [],
        };
    });
    const footerLabels = {
        firstLabel: get(footer, 'notesLabel'),
        secondLabel: get(footer, 'legalTermsLabel'),
    };
    const InvoiceInfoView = (props) => isMobileView() ? (React.createElement("div", { className: `${s.detail} ${s.customInvoiceInfo}` }, props.children)) : (React.createElement(React.Fragment, null, props.children));
    return (React.createElement("div", { className: s.invoiceContainer },
        React.createElement("div", { className: s.hideOnPrint }, recurringInvoiceViewEnabled && (React.createElement(RecurringInvoiceView, { recurringStandaloneViewEnabled: recurringStandaloneViewEnabled, invoice: invoice, darkView: darkView, isReady: invoiceReady, harmonyThemeEnabled: harmonyThemeEnabled }))),
        React.createElement("div", { className: `${s.hideOnPrint} ${isStatusDisplayed({ status, isUoU }) && s.hangingStatus}` },
            React.createElement(Status, { status: status, isUoU: isUoU })),
        React.createElement("div", { className: s.priceQuoteView },
            React.createElement("div", { className: `${s.details}`, style: { padding: '48px', paddingBottom: '0px' } },
                React.createElement(Layout, { gap: 18 },
                    React.createElement(Cell, { span: 4 },
                        React.createElement(Logo, { logo: businessInfo.logo, onImageReady: onInvoiceReady })),
                    React.createElement(Cell, { span: 8 },
                        React.createElement(InvoiceInfoView, null,
                            React.createElement(InvoiceInfo, { documentCustomFields: documentCustomFields, invoice: invoice, isRecurring: recurringInvoiceViewEnabled, status: status, isReady: invoiceReady, customInvoiceNumberLabel: customInvoiceNumberLabel, customFields: customFields, primarySiteLocale: primarySiteLocale }))),
                    React.createElement(Cell, { span: 12 },
                        React.createElement(InvoicesBusinessInfo, { businessInfo: businessInfo, isReady: businessInfoReady, businessCustomFields: businessCustomFields })),
                    React.createElement(Cell, { span: 12 },
                        React.createElement(Divider, null)),
                    React.createElement(Cell, { span: 12 },
                        React.createElement(Customer, { customer: invoice.customer, isReady: invoiceReady })),
                    React.createElement(Cell, { span: 12 },
                        React.createElement(Title, { invoice: invoice, isReady: invoiceReady })))),
            React.createElement(LineItems, { lineItems: lineItemList, currencyCode: invoice.currencyCode ?? DEFAULT_CURRENCY_CODE, locale: (invoice.locale && invoice.locale.language) ?? DEFAULT_LOCALE, showTaxColumn: showTaxPerLineItem, tableStyle: taxSettingsEnabled ? 'grid' : 'legacy' }),
            React.createElement(Totals, { showTaxBreakdown: showTaxBreakdown, invoice: invoice, taxSettingsEnabled: taxSettingsEnabled, showItemsSubtotalPerTax: showItemsSubtotalPerTax, taxAdjustmentsEnabled: taxAdjustmentsEnabled }),
            paymentsSectionEnabled && showTransactionDetails && (React.createElement(PaymentsReceived, { invoice: invoice })),
            React.createElement(Comments, { metaData: invoice.metaData, footer: footerLabels }),
            ascend && (React.createElement("div", { className: s.ascendFooter, "data-hook": "ascend-footer" },
                React.createElement(Text, { weight: "normal", size: "tiny", secondary: true, light: true }, t('invoice-view:price-quote.created-with')),
                React.createElement("div", { className: s.ascendLogo },
                    React.createElement(AscendLogo, null)))))));
};
const InvoicePreviewInfo = withTranslation(['invoice-view', 'locale-data'])(PreviewInfo);
export { InvoicePreviewInfo };
//# sourceMappingURL=InvoicePreviewInfo.js.map